import { ArrowRight } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  DataTable,
  FilterBar,
  SelectChip,
  SplitList,
  StatusChip,
  type DataTableState,
  type IDataColumn
} from '@/design';
import { formatDateTime } from '@/domains/survey';
import type {
  IIdentifierCheck,
  IIdentifierCheckQuery
} from '../../api/identifier-check-contracts';
import { identifierChecksApi } from '../../api/identifier-checks-api';
import type { IPage } from '../../api/oversight-contracts';
import {
  AdminPage,
  LoadError,
  Pager,
  PastTheEnd,
  pageOf,
  useAdminData,
  withPage
} from '../../components/admin-page';
import { ListArea, usePaneRead } from '../../components/list-area';
import type { ICardLinkState } from '../../components/participant-link';
import {
  CHECK_ID_TYPES,
  CHECK_REASONS,
  CHECK_STATUSES,
  checkNumberText,
  checkReasonLabel,
  checkStatusLabel,
  checkStatusTone
} from '../../number-checks';
import { TEAM_PATHS, numberCheckKey, numberCheckPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { useAssignees } from '../compliance/compliance-page';

/**
 * Number checks (SR-487, PFR §4; ruling 15): every number registration could not corroborate —
 * FINRA's miss, its outage, an NPN or licence no registry is asked about, a claim — for staff to
 * verify, reject or replace, the check opened beside the list (`AdminNumberChecksArea`) as a work
 * item is beside the compliance queues. Read under `responses.browse`, the route's guard.
 *
 * **The URL is the state**, as on every Team Portal list: `status`, `reason`, `idType` (each
 * repeatable), `assignee` (`me`, `none` or an account id) and `page`. A value no position stands
 * for is still sent and drawn as spelled — survey-api's 400 `invalid-query` names it, in its words
 * (`refusalMessage`) — and a page is drawn only under the query it answers. A read of the query
 * on screen that fails over its rows keeps them, under "Could not refresh".
 *
 * **Open by default** (ruling 15): with no `status` the list asks for open checks, and the Status
 * filter shows Open. `status=all` is this page's own value, as Compliance's `view` is: every
 * status, sent as none.
 *
 * survey-api orders the checks newest first, and offers no participant filter or search, so the
 * list has neither.
 */
export function AdminNumberChecksPage() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const query = numberChecksQueryOf(params);
  const asked = JSON.stringify(query);
  const { data, error } = useAdminData<{ asked: string; page: IPage<IIdentifierCheck> }>(
    async (signal) => {
      try {
        return { asked, page: await identifierChecksApi.list(query, signal) };
      } catch (listError) {
        throw new Error(refusalMessage(listError, 'Could not load this page'));
      }
    },
    [asked]
  );
  // The last answer while the next is out; nothing once this query's read has failed.
  const shown = error && data?.asked !== asked ? null : data;
  const { assignees } = useAssignees();
  const assigneeOptions = [
    ...ASSIGNEES,
    ...assignees.map((account) => ({ value: account.accountId, label: account.email }))
  ];

  /** A filter change starts at page 1: page 3 of the old query means nothing. */
  const choose = (name: string) => (value: Nullable<string>) => {
    const next = new URLSearchParams(params);
    next.delete(name);
    if (value !== null) next.set(name, value);
    next.delete('page');
    setParams(next);
  };
  const turnTo = (target: number) => setParams(withPage(params, target));

  const status = params.get('status');
  const narrowed = [...params.keys()].some((name) => name !== 'page');
  const tableState: DataTableState | undefined =
    shown === null
      ? error
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : undefined;

  return (
    <AdminPage title="Number checks">
      <FilterBar
        onClear={narrowed ? () => setParams(new URLSearchParams()) : undefined}
        count={shown ? `${shown.page.total} found` : undefined}
      >
        <SelectChip
          label="Status"
          any="Every status"
          // Absent is Open (the module's JSDoc); `all` is the chip's "Every status".
          value={status === null ? 'open' : status === ALL ? null : status}
          options={CHECK_STATUSES}
          onChoose={(value) => choose('status')(value ?? ALL)}
        />
        <SelectChip
          label="Reason"
          any="Every reason"
          value={params.get('reason')}
          options={CHECK_REASONS}
          onChoose={choose('reason')}
        />
        <SelectChip
          label="Number type"
          any="Every number"
          value={params.get('idType')}
          options={CHECK_ID_TYPES}
          onChoose={choose('idType')}
        />
        <SelectChip
          label="Assigned to"
          any="Anyone"
          value={params.get('assignee') || null}
          options={assigneeOptions}
          onChoose={choose('assignee')}
        />
      </FilterBar>

      {error && shown !== null && (
        <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
      )}

      <NumberChecksTable
        rows={shown?.page.items ?? []}
        state={tableState}
        search={location.search}
        empty={
          shown && shown.page.total > 0 ? (
            <PastTheEnd
              page={shown.page.page}
              pageSize={shown.page.pageSize}
              total={shown.page.total}
              counted={['check', 'checks']}
              onPage={turnTo}
            />
          ) : narrowed ? (
            'No number check matches this filter.'
          ) : (
            'No number waits for a check.'
          )
        }
      />

      {shown && (
        <Pager
          page={shown.page.page}
          pageSize={shown.page.pageSize}
          total={shown.page.total}
          onPage={turnTo}
        />
      )}
    </AdminPage>
  );
}

/** `status=all`: every status — the page's own value (the page's JSDoc). */
const ALL = 'all';

/** The Assigned to filter's positions that need no read, Compliance's words. Each colleague who
 *  can work a check follows by address (`useAssignees`, Compliance's); an account id none of them
 *  holds is drawn as spelled. */
const ASSIGNEES = [
  { value: 'me', label: 'Me' },
  { value: 'none', label: 'Unassigned' }
];

/** The URL's filters as the list's query — one function for the list and its pane. */
function numberChecksQueryOf(
  params: URLSearchParams,
  page = pageOf(params.get('page'))
): IIdentifierCheckQuery {
  const statuses = params.getAll('status');
  return {
    status: statuses.length === 0 ? ['open'] : statuses.filter((status) => status !== ALL),
    reason: params.getAll('reason'),
    idType: params.getAll('idType'),
    assignee: params.get('assignee') || undefined,
    page
  };
}

/** The checks as a table whose rows open the check beside the list. */
function NumberChecksTable({
  rows,
  state,
  search,
  empty
}: {
  rows: IIdentifierCheck[];
  state: DataTableState | undefined;
  search: string;
  empty: React.ReactNode;
}) {
  const navigate = useNavigate();
  const linkState: ICardLinkState = { search, list: true };
  const columns: IDataColumn<IIdentifierCheck>[] = [
    {
      id: 'participant',
      header: 'Participant',
      cell: (check) => (
        <span className="font-medium text-[var(--color-royal-blue)]">
          {check.participant.displayName}
        </span>
      )
    },
    { id: 'number', header: 'Number', cell: (check) => checkNumberText(check) },
    { id: 'reason', header: 'Reason', cell: (check) => checkReasonLabel(check.reason) },
    {
      id: 'status',
      header: 'Status',
      cell: (check) => <CheckStatusChip status={check.status} />
    },
    {
      id: 'assignee',
      header: 'Assigned to',
      cell: (check) => check.assignee?.email ?? 'Unassigned'
    },
    { id: 'raised', header: 'Raised', cell: (check) => formatDateTime(check.createdAt) },
    {
      id: 'open',
      header: <span className="sr-only">Open</span>,
      label: 'Open',
      headClassName: 'w-10',
      cell: () => <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
    }
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows.length === 0 ? null : rows}
      rowKey={(check) => check.checkId}
      onOpen={(check) =>
        navigate(numberCheckPath(check.participant.participantId, check.checkId), {
          state: linkState
        })
      }
      state={state ?? (rows.length === 0 ? { kind: 'empty', content: empty } : undefined)}
      label="Number checks"
    />
  );
}

/** A check's status, Open marked as work to do. */
export function CheckStatusChip({ status }: { status: string }) {
  return (
    <StatusChip tone={checkStatusTone(status)} dot={status === 'open'}>
      {checkStatusLabel(status)}
    </StatusChip>
  );
}

/** Number checks and a check, as one area: the list, or a check with the list beside it. */
export function AdminNumberChecksArea() {
  return (
    <ListArea
      param="checkKey"
      list={TEAM_PATHS.numberChecks}
      paneTitle="Number checks"
      pane={(search, selected) => <NumberChecksPane search={search} selected={selected} />}
    />
  );
}

/** The list as the pane beside a check draws it: whose number, which, and its status. */
function NumberChecksPane({ search, selected }: { search: string; selected: string }) {
  const { shown, pager, status } = usePaneRead(
    search,
    'No number check on this list.',
    (params, page, signal) => identifierChecksApi.list(numberChecksQueryOf(params, page), signal)
  );
  return (
    <SplitList
      label="Number checks"
      selected={selected}
      items={
        shown?.items.map((check) => ({
          key: numberCheckKey(check.participant.participantId, check.checkId),
          to: numberCheckPath(check.participant.participantId, check.checkId),
          state: { search, list: true } satisfies ICardLinkState,
          primary: check.participant.displayName,
          secondary: checkNumberText(check),
          trailing: <CheckStatusChip status={check.status} />
        })) ?? null
      }
      status={status}
      footer={pager}
    />
  );
}
